import { useEffect } from 'react';
import { useParticipant } from '../contexts/ParticipantContext';
import type { PersonaId } from '../contexts/ParticipantContext';

/**
 * Hook to apply accessibility features based on persona
 *
 * This hook:
 * 1. Reads the current persona from ParticipantContext
 * 2. Applies a data-persona attribute to the root HTML element
 * 3. CSS in persona-accessibility.css uses this attribute to:
 *    - Apply 200% zoom for John (baixa visão) and Emily (fotofobia)
 *    - Apply high contrast for John
 *    - Apply dark mode for Emily
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   usePersonaAccessibility();
 *   return <div>...</div>;
 * }
 * ```
 */
export function usePersonaAccessibility() {
  const { state } = useParticipant();
  const persona: PersonaId | null = state.session?.determined_persona || null;

  useEffect(() => {
    if (!persona) {
      // No persona determined yet, clear any existing attribute
      document.documentElement.removeAttribute('data-persona');
      return;
    }

    // Apply persona data attribute to root element
    // CSS will use this to apply:
    // - john_smith: font-size: 200% + high contrast
    // - dra_emily: font-size: 200% + dark mode
    // - olivia/dr_charles: default (100%)
    document.documentElement.setAttribute('data-persona', persona);

    // Log for debugging (can be removed in production)
    console.log(`[Accessibility] Applied persona: ${persona}`);

    // Cleanup function
    return () => {
      document.documentElement.removeAttribute('data-persona');
    };
  }, [persona]);

  // Return persona info for conditional rendering if needed
  return {
    persona,
    hasZoom200: persona === 'john_smith' || persona === 'dra_emily',
    hasHighContrast: persona === 'john_smith',
    hasDarkMode: persona === 'dra_emily',
  };
}
